// NEW_FILE_JUSTIFIED: Step 2 of claim wizard — dynamic form from template, extracted to stay under 200 lines
import { useEffect, useRef } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: components in src/components/insurance/ require three levels up to reach convex/_generated/
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: standard convex _generated import pattern
import { format, parse } from 'date-fns';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, ArrowRight, CalendarIcon, Plus, Save, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { IClaimFormState } from '@/hooks/useClaimForm';
import type { IClaimFormSectionConfig } from '@/@types/claims';

// LOCAL_TYPE_JUSTIFIED: step-internal props, not reused outside wizard
interface IClaimWizardStepFormProps {
  orgId: Id<'organisations'>;
  policyId: Id<'insurancePolicies'>;
  insuranceTypeId: Id<'orgInsuranceTypes'> | undefined;
  state: IClaimFormState;
  onField: (field: keyof IClaimFormState, value: string) => void;
  onCustomField: (key: string, value: string) => void;
  onAddLineItem: () => void;
  onUpdateLineItem: (index: number, field: string, value: string) => void;
  onRemoveLineItem: (index: number) => void;
  onNext: () => void;
  onPrev: () => void;
  onSaveDraft?: () => void;
  isSavingDraft?: boolean;
}

const TREATMENT_OPTIONS = ['Outpatient', 'Dental', 'Optical', 'Maternity', 'Inpatient', 'Wellness'];

function DatePickerField({
  value,
  onChange,
  placeholder,
  testId,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  testId?: string;
}) {
  const dateValue = value ? parse(value, 'yyyy-MM-dd', new Date()) : undefined;
  const isValid = dateValue && !isNaN(dateValue.getTime());

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          data-testid={testId}
          className={cn(
            'w-full justify-start text-left font-normal !h-10',
            !value && 'text-muted-foreground'
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {isValid ? format(dateValue, 'dd/MM/yyyy') : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={isValid ? dateValue : undefined}
          onSelect={day => {
            if (day) onChange(format(day, 'yyyy-MM-dd'));
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

function CoreFields({
  state,
  onField,
}: {
  state: IClaimFormState;
  onField: (field: keyof IClaimFormState, value: string) => void;
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="space-y-1.5">
        <Label>
          Treatment Type <span className="text-destructive">*</span>
        </Label>
        <Select value={state.treatmentType} onValueChange={v => onField('treatmentType', v)}>
          <SelectTrigger data-testid="claim-treatmentType">
            <SelectValue placeholder="Select type" />
          </SelectTrigger>
          <SelectContent>
            {TREATMENT_OPTIONS.map(o => (
              <SelectItem key={o} value={o}>
                {o}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label>
          Date of Service <span className="text-destructive">*</span>
        </Label>
        <DatePickerField
          value={state.dateOfService}
          onChange={v => onField('dateOfService', v)}
          placeholder="Pick date of service"
          testId="claim-dateOfService"
        />
      </div>
      <div className="space-y-1.5">
        <Label>Date of Injury / Illness</Label>
        <DatePickerField
          value={state.dateOfInjuryOrIllness}
          onChange={v => onField('dateOfInjuryOrIllness', v)}
          placeholder="Pick date"
          testId="claim-dateOfInjuryOrIllness"
        />
      </div>
      <div className="space-y-1.5">
        <Label>Provider Name</Label>
        <Input
          value={state.providerName}
          onChange={e => onField('providerName', e.target.value)}
          placeholder="Clinic or hospital"
          data-testid="claim-providerName"
        />
      </div>
      <div className="space-y-1.5 sm:col-span-2">
        <Label>Diagnosis</Label>
        <Textarea
          rows={2}
          value={state.diagnosis}
          onChange={e => onField('diagnosis', e.target.value)}
          placeholder="Medical diagnosis"
          data-testid="claim-diagnosis"
        />
      </div>
      <div className="space-y-1.5 sm:col-span-2">
        <Label>Symptoms</Label>
        <Textarea
          rows={2}
          value={state.symptoms}
          onChange={e => onField('symptoms', e.target.value)}
          placeholder="Described symptoms"
          data-testid="claim-symptoms"
        />
      </div>
    </div>
  );
}

function TemplateSections({
  sections,
  customFieldValues,
  onCustomField,
}: {
  sections: IClaimFormSectionConfig[];
  customFieldValues: Record<string, string>;
  onCustomField: (key: string, value: string) => void;
}) {
  const visibleSections = sections.filter(s => s.visible).sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <>
      {visibleSections.map(section => {
        const visibleFields = section.fields
          .filter(f => f.visible)
          .sort((a, b) => a.sortOrder - b.sortOrder);
        if (visibleFields.length === 0) return null;
        return (
          <div key={section.sectionKey} className="space-y-3">
            <Separator />
            <p className="text-sm font-semibold text-foreground">{section.label}</p>
            <div className="grid gap-3 sm:grid-cols-2">
              {visibleFields.map(field => (
                <div
                  key={field.fieldKey}
                  className={cn('space-y-1.5', field.options && field.options.length > 0 ? '' : '')}
                >
                  <Label>
                    {field.label}
                    {field.required && <span className="ml-1 text-destructive">*</span>}
                  </Label>
                  {field.options && field.options.length > 0 ? (
                    <Select
                      value={customFieldValues[field.fieldKey] ?? ''}
                      onValueChange={v => onCustomField(field.fieldKey, v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        {field.options.map(o => (
                          <SelectItem key={o} value={o}>
                            {o}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      value={customFieldValues[field.fieldKey] ?? ''}
                      onChange={e => onCustomField(field.fieldKey, e.target.value)}
                      required={field.required}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </>
  );
}

export function ClaimWizardStepForm({
  orgId,
  policyId,
  insuranceTypeId,
  state,
  onField,
  onCustomField,
  onAddLineItem,
  onUpdateLineItem,
  onRemoveLineItem,
  onNext,
  onPrev,
  onSaveDraft,
  isSavingDraft,
}: IClaimWizardStepFormProps) {
  const templates = useQuery(
    api.insurance.claimFormTemplates.list,
    insuranceTypeId ? { orgId, insuranceTypeId } : { orgId }
  );
  const template = templates?.[0];
  const canSubmit = !!state.treatmentType && !!state.dateOfService;

  // Fetch policy and employee data for field pre-fill
  const policyData = useQuery(api['insurance/policies'].get, { policyId });
  const employeeData = useQuery(
    api.companies.employees.get,
    policyData?.companyEmployeeId ? { employeeId: policyData.companyEmployeeId } : 'skip'
  );

  // Stable refs so the effect below can read latest values without re-running on each field change.
  // Assigning to .current during render keeps the ref in sync before the prefill effect runs.
  const onCustomFieldRef = useRef(onCustomField);
  // eslint-disable-next-line react-hooks/refs -- intentional "latest value" ref pattern; effect below reads via ref to avoid stale closures without adding callbacks to its dep array
  onCustomFieldRef.current = onCustomField;
  const customFieldValuesRef = useRef(state.customFieldValues);
  // eslint-disable-next-line react-hooks/refs -- intentional "latest value" ref pattern; same reason as above
  customFieldValuesRef.current = state.customFieldValues;

  // Pre-fill custom field values once when template + policy data are available
  const prefillDoneRef = useRef(false);
  useEffect(() => {
    if (prefillDoneRef.current) return;
    if (!template || policyData === undefined) return;

    const prefill: Record<string, string> = {};
    prefill.policy_number = policyData.policyNumber ?? '';
    const memberId = policyData.fieldValues.find(
      (f: { fieldLabel: string; value?: string }) =>
        f.fieldLabel.toLowerCase() === 'member id' || f.fieldLabel.toLowerCase() === 'memberid'
    )?.value;
    if (memberId) prefill.member_id = memberId;
    if (employeeData) {
      const nameParts = employeeData.name.split(' ');
      prefill.first_name = nameParts.slice(0, -1).join(' ') || employeeData.name;
      prefill.last_name = nameParts.slice(-1)[0] ?? '';
      if (employeeData.email) prefill.email = employeeData.email;
      if (employeeData.phoneNumber) prefill.phone = employeeData.phoneNumber;
      if (employeeData.dateOfBirth) prefill.date_of_birth = employeeData.dateOfBirth;
    }

    const currentValues = customFieldValuesRef.current;
    for (const [key, value] of Object.entries(prefill)) {
      if (value && !currentValues[key]) {
        onCustomFieldRef.current(key, value);
      }
    }
    prefillDoneRef.current = true;
  }, [template, policyData, employeeData]);

  return (
    <div className="space-y-6">
      <CoreFields state={state} onField={onField} />
      {template && template.sections.length > 0 && (
        <TemplateSections
          sections={template.sections as IClaimFormSectionConfig[]} // AS_JUSTIFIED: Convex return narrowed to typed IClaimFormSectionConfig[] matching return validator
          customFieldValues={state.customFieldValues}
          onCustomField={onCustomField}
        />
      )}
      <Separator />
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold">Line Items</p>
          <Button type="button" variant="outline" size="sm" onClick={onAddLineItem}>
            <Plus className="mr-1 h-4 w-4" /> Add Item
          </Button>
        </div>
        {state.lineItems.map((item, idx) => (
          <div key={item.tempId} className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-end">
            <Input
              placeholder="Description"
              value={item.description}
              onChange={e => onUpdateLineItem(idx, 'description', e.target.value)}
            />
            <Input
              type="number"
              placeholder="Amount"
              className="w-28"
              value={item.amount}
              onChange={e => onUpdateLineItem(idx, 'amount', e.target.value)}
            />
            <Input
              placeholder="EUR"
              className="w-20"
              value={item.currency}
              onChange={e => onUpdateLineItem(idx, 'currency', e.target.value)}
            />
            <Button type="button" variant="ghost" size="icon" onClick={() => onRemoveLineItem(idx)}>
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        ))}
      </div>
      <div className="flex justify-between pt-2">
        <Button variant="outline" onClick={onPrev}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
        {onSaveDraft && (
          <Button variant="outline" onClick={onSaveDraft} disabled={isSavingDraft}>
            <Save className="mr-2 h-4 w-4" /> Save Draft
          </Button>
        )}
        <Button onClick={onNext} disabled={!canSubmit} data-testid="claim-wizard-next">
          Next <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
